import React, { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Crown, Check, X, Sparkles, MessageCircle, CloudRain, Camera, Calendar,
  Tag, Award, MapPin, Bell, Compass, Rocket,
} from "lucide-react";

/**
 * /share/free-vs-pro — Side-by-side comparison reel.
 *
 * Three 9:16 scenes for screen-recording:
 *   Scene 1 — "What's free / what's Pro" big comparison table
 *   Scene 2 — "Why Pro makes ALL the difference" (3 hero benefits)
 *   Scene 3 — "Try Pro free for 14 days" QR + CTA
 *
 * Caption pack underneath for one-tap social sharing.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const TRIAL_URL = `${PUBLIC_URL}/register?ref=free-vs-pro&trial=1`;

const FREE_FEATURES = [
  "Browse 22,000+ UK campsites",
  "Filter by region, dogs, EHU",
  "See prices side-by-side",
  "Read camper reviews",
  "Trip Wall (read-only)",
  "Save up to 5 favourites",
];

const PRO_FEATURES = [
  "Everything in Free, plus:",
  "Camper AI chat (24/7 advice)",
  "AI packing list (per trip)",
  "Weather alerts before every trip",
  "Date-based availability search",
  "Partner discount codes",
  "Photo uploads to Trip Wall",
  "Forward 14-day outlook",
  "Pro badge in community",
  "Early access to new features",
];

function Sticker({ children, color = "#ff4500", textColor = "#ffffff" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em]"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden"
        style={{
          aspectRatio: "9 / 16",
          maxWidth: 480,
          backgroundImage: bg,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid={testid}
      >
        {children}
        <div
          className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/65 backdrop-blur-sm text-white text-[10px] sm:text-[11px] font-bold mono uppercase tracking-[0.22em] text-center pointer-events-none z-10"
          aria-hidden="true"
        >
          🌐 camp-compass.uk
        </div>
      </section>
    </div>
  );
}

export default function FreeVsProReel() {
  const captions = useMemo(() => [
    {
      title: "Caption · Free vs Pro signature post",
      body: `🏕 What you get on CampCompass UK — free vs Pro 👇

FREE while we grow:
✓ 22,000+ UK campsites
✓ Filter by dogs, electric, region
✓ Side-by-side price comparison
✓ Trip Wall + community reviews
✓ Save 5 favourites

PRO (14 days FREE today):
✓ Everything above, plus...
🤖 Camper AI chat — 24/7 trip advice
🎒 AI packing list — bespoke per trip
🌦 Weather alerts before every trip
📅 Date-based availability search
💸 Partner discount codes
📸 Trip Wall photo uploads
🏅 Pro badge in community

👉 camp-compass.uk · 14 days free · cancel anytime

#ukcamping #campsiteuk #ukcamping2026`,
    },
    {
      title: "Caption · 'Why bother with Pro' (TikTok angle)",
      body: `Why bother with Pro? 🤔

Free is amazing — 22,000 UK campsites, comparisons, community.

Pro is the one that saves your weekend:

→ AI tells you exactly what to pack for THAT site, THAT weather
→ 48-hr storm alert texts before you leave
→ Bespoke 14-day weather outlook for the booking
→ Discount codes from outdoor brands

14 days FREE today. Cancel in one tap.

👉 camp-compass.uk

#ukcamping #campervan #ukstaycation`,
    },
    {
      title: "Caption · Short hook for Reels",
      body: `Free: browse 22k UK campsites.
Pro: never get caught in a storm.

14 days FREE today → camp-compass.uk

#ukcamping #campingweekend`,
    },
  ], []);

  return (
    <div className="bg-[#0e0e0e] min-h-screen text-white" data-testid="free-vs-pro-page">
      <header className="max-w-3xl mx-auto px-5 py-8">
        <div className="eyebrow text-[#ff4500] mb-2 flex items-center gap-2">
          <Crown className="w-3.5 h-3.5" /> Free vs Pro reel
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3">Free or Pro?<br />Show the difference.</h1>
        <p className="text-sm text-[#bbb] leading-relaxed max-w-xl">
          Three 9:16 scenes — Free vs Pro comparison, three Pro power-features that justify the trial,
          and a "try free 14 days" QR. Each scene is built to be screen-recorded for 3-4 seconds.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 px-3 pb-10 max-w-7xl mx-auto justify-items-center">

        {/* SCENE 1 — Comparison */}
        <Scene
          label="Scene 1 · the comparison"
          testid="fp-scene-1"
          bg="linear-gradient(180deg, #0e0e0e 0%, #1a1a1a 100%)"
        >
          <div className="p-5 pt-8 text-center">
            <Sticker color="#ffffff" textColor="#0e0e0e">
              <Compass className="w-3 h-3" /> What you get
            </Sticker>
            <div className="font-display text-[36px] sm:text-[44px] leading-[0.95] mt-3">
              Free vs <span className="text-[#ff4500]">Pro</span>
            </div>
          </div>
          <div className="px-3 grid grid-cols-2 gap-2 -mt-2">
            <div className="border hairline border-white/20 bg-white/5 p-2.5">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-white/85 mb-1.5 font-bold">FREE</div>
              {FREE_FEATURES.map((f, i) => (
                <div key={i} className="flex items-start gap-1 text-[10px] sm:text-[11px] mb-1 leading-tight">
                  <Check className="w-2.5 h-2.5 mt-0.5 text-white/85 flex-shrink-0" strokeWidth={3} />
                  <span className="text-white/95">{f}</span>
                </div>
              ))}
            </div>
            <div className="border-2 border-[#ff4500] bg-[#ff4500]/20 p-2.5">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-[#ffe7d6] mb-1.5 font-bold">PRO 🏅</div>
              {PRO_FEATURES.map((f, i) => (
                <div key={i} className="flex items-start gap-1 text-[10px] sm:text-[11px] mb-1 leading-tight">
                  <Check className="w-2.5 h-2.5 mt-0.5 text-[#ff4500] flex-shrink-0" strokeWidth={3} />
                  <span className={i === 0 ? "text-[#ffe7d6] font-bold" : "text-white"}>{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="p-5 text-center">
            <div className="inline-block px-3 py-1.5 bg-[#ff4500] text-white font-display text-base">
              14 days free · cancel anytime
            </div>
          </div>
        </Scene>

        {/* SCENE 2 — Why Pro */}
        <Scene
          label="Scene 2 · why Pro"
          testid="fp-scene-2"
          bg="linear-gradient(180deg, #ff4500 0%, #b73200 100%)"
        >
          <div className="p-5 pt-8">
            <Sticker color="#ffffff" textColor="#ff4500">
              <Sparkles className="w-3 h-3" /> Why Pro
            </Sticker>
          </div>
          <div className="px-5">
            <div className="font-display text-[42px] sm:text-[52px] leading-[0.92] mb-4 text-center">
              Pro makes<br />all the<br />difference.
            </div>
            <div className="space-y-2.5">
              <ProRow icon={<MessageCircle className="w-5 h-5" />} title="Camper AI" body="24/7 trip advice in your pocket" />
              <ProRow icon={<CloudRain className="w-5 h-5" />} title="Storm alerts" body="Texts 48h before bad weather hits" />
              <ProRow icon={<Tag className="w-5 h-5" />} title="Partner deals" body="Discount codes from outdoor brands" />
            </div>
          </div>
          <div className="p-5 text-center">
            <div className="text-[11px] mono uppercase tracking-[0.22em] text-white/95">
              14 DAYS FREE — start today
            </div>
          </div>
        </Scene>

        {/* SCENE 3 — Trial CTA + QR */}
        <Scene
          label="Scene 3 · try free"
          testid="fp-scene-3"
          bg="linear-gradient(180deg, #0e0e0e 0%, #2a1a0e 50%, #ff4500 100%)"
        >
          <div className="p-5 pt-8 flex justify-between gap-2">
            <Sticker color="#ffffff" textColor="#0e0e0e">
              <Crown className="w-3 h-3" /> 14 days FREE
            </Sticker>
            <Sticker color="#ff4500">
              <Rocket className="w-3 h-3" /> Try Pro
            </Sticker>
          </div>
          <div className="px-5 text-center">
            <div className="font-display text-[52px] sm:text-[68px] leading-[0.92] drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]">
              TRY PRO<br />FREE
            </div>
            <div className="inline-block font-display text-xl sm:text-2xl px-3 py-1 mt-3" style={{ background: "#ffffff", color: "#0e0e0e" }}>
              14 days · cancel anytime
            </div>
          </div>
          <div className="p-4 space-y-3" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.88) 100%)" }}>
            <div className="flex items-center justify-between gap-3">
              <div className="text-left min-w-0">
                <div className="text-[10px] mono uppercase tracking-[0.22em] text-white/90 mb-1">Scan to start</div>
                <div className="font-display text-base sm:text-lg leading-tight break-all">camp-compass.uk</div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-[#ffe7d6] mt-1">20-second signup</div>
              </div>
              <div className="bg-white p-1.5 shrink-0" data-testid="fp-qr">
                <QRCodeSVG value={TRIAL_URL} size={104} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
              </div>
            </div>
          </div>
        </Scene>
      </div>

      <CaptionPack captions={captions} testid="fp-captions" />
    </div>
  );
}

function ProRow({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3 bg-white/15 backdrop-blur p-2.5">
      <div className="flex-shrink-0 mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="font-display text-base sm:text-lg leading-tight">{title}</div>
        <div className="text-[11px] sm:text-xs text-white/95 leading-snug">{body}</div>
      </div>
    </div>
  );
}

export function CaptionPack({ captions, testid }) {
  return (
    <section className="max-w-3xl mx-auto px-5 pb-20 space-y-5" data-testid={testid}>
      <div className="border-l-2 border-[#ff4500] pl-4">
        <h2 className="font-display text-2xl mb-2">Caption pack — tap to copy</h2>
        <p className="text-sm text-[#bbb]">Ready-to-paste social captions. Pick one for the headline post, others for stories / replies.</p>
      </div>
      {captions.map((c, i) => (
        <article key={i} className="p-5 border hairline bg-[#1a1a1a]" data-testid={`${testid}-${i+1}`}>
          <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500] mb-2">{c.title}</div>
          <pre className="bg-[#0e0e0e] p-3 mb-3 whitespace-pre-wrap text-sm leading-relaxed font-sans">{c.body}</pre>
          <button
            type="button"
            onClick={() => { try { navigator.clipboard.writeText(c.body); } catch { /* no clipboard */ } }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#ff4500] hover:bg-[#e03e00] text-white text-xs font-bold mono uppercase tracking-[0.14em] transition-colors"
            data-testid={`${testid}-copy-${i+1}`}
          >
            <Sparkles className="w-3.5 h-3.5" /> Copy caption
          </button>
        </article>
      ))}
    </section>
  );
}
